import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';

// Helper functions
const getCategoriesNameById = (id, categoriesData) => {
if (!categoriesData || !Array.isArray(categoriesData)) return 'Unknown';
const category = categoriesData.find(c => parseInt(c.id) === parseInt(id));
return category ? category.name : 'Unknown';
};

const formatCurrency = (amount) => {
return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
}).format(amount || 0) + " "+"sh";
};

const formatNumberWithComma = (number) => {
return new Intl.NumberFormat('en-US').format(number || 0);
};

const formatDate = (dateString) => {
if (!dateString) return 'N/A';
return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
});
};

// Calculate profit for an item
const calculateProfit = (item) => {
return ((item.selling || 0) - (item.cost || 0)) * (item.quantity || 0);
};

// Calculate total value for an item
const calculateTotalValue = (item) => {
return (item.quantity || 0) * (item.selling || 0);
};

// Manual table drawing function for PDF
const drawReportTable = (doc, data, startY, reportType) => {
const pageWidth = doc.internal.pageSize.width;
const margin = 14;
const rowHeight = 10;

let colWidths = [];
let headers = [];

switch (reportType) {
    case 'detailed':
    colWidths = [8, 20, 25, 35, 15, 20, 20, 25, 25, 20];
    headers = ['#', 'Date', 'Supplier', 'Stock Item', 'Qty', 'Cost', 'Selling', 'Total Cost', 'Total Selling', 'Profit'];
    break;
    case 'supplier':
    colWidths = [10, 35, 15, 20, 25, 25, 25];
    headers = ['#', 'Supplier', 'Items', 'Quantity', 'Total Cost', 'Total Value', 'Profit'];
    break;
    case 'stock':
    colWidths = [10, 30, 25, 15, 20, 20, 25, 25];
    headers = ['#', 'Stock Item', 'Supplier', 'Quantity', 'Avg Cost', 'Avg Selling', 'Total Value', 'Profit'];
    break;
    default:
    colWidths = [10, 25, 35, 15, 20, 20, 25];
    headers = ['#', 'Supplier', 'Stock Item', 'Qty', 'Cost', 'Selling', 'Value'];
}

let currentY = startY;
const neededHeight = (data.length + 1) * rowHeight;
if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
    doc.addPage();
    currentY = margin;
}

// Draw table header
doc.setFillColor(25, 118, 210);
doc.setTextColor(255, 255, 255);
doc.setFont(undefined, 'bold');

let xPos = margin;

// Draw header background
doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

// Draw header text
headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
});

currentY += rowHeight;

// Draw table rows
doc.setFont(undefined, 'normal');
doc.setTextColor(0, 0, 0);

data.forEach((row, rowIndex) => {
    const actualRowHeight = rowHeight;

    // Check for page break
    if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
    doc.addPage();
    currentY = margin;
    
    // Redraw header
    doc.setFillColor(25, 118, 210);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    xPos = margin;
    headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
    });
    
    currentY += rowHeight;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
    doc.setFillColor(248, 248, 248);
    } else {
    doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw row data based on report type
    let cells = [];
    
    switch (reportType) {
    case 'detailed':
        const supplierName = row.supplier_name || getCategoriesNameById(row.supplier_id, data.supplierNames) || 'Unknown';
        const stockName = row.stock_name || 'Unknown';
        
        cells.push(
        (rowIndex + 1).toString(),
        formatDate(row.stock_date),
        supplierName.substring(0, 20),
        stockName.substring(0, 25),
        formatNumberWithComma(row.quantity || 0),
        formatCurrency(row.cost || 0),
        formatCurrency(row.selling || 0),
        formatCurrency((row.quantity || 0) * (row.cost || 0)),
        formatCurrency(calculateTotalValue(row)),
        formatCurrency(calculateProfit(row))
        );
        break;
    case 'supplier':
        cells.push(
        (rowIndex + 1).toString(),
        (row.supplier_name || 'Unknown').substring(0, 30),
        formatNumberWithComma(row.items_count || 0),
        formatNumberWithComma(row.total_quantity || 0),
        formatCurrency(row.total_cost || 0),
        formatCurrency(row.total_value || 0),
        formatCurrency(row.total_profit || 0)
        );
        break;
    case 'stock':
        cells.push(
        (rowIndex + 1).toString(),
        (row.stock_name || 'Unknown').substring(0, 25),
        (row.supplier_name || 'Unknown').substring(0, 20),
        formatNumberWithComma(row.total_quantity || 0),
        formatCurrency(row.avg_cost || 0),
        formatCurrency(row.avg_selling || 0),
        formatCurrency(row.total_value || 0),
        formatCurrency(row.total_profit || 0)
        );
        break;
    default:
        const supplierNameDefault = row.supplier_name || getCategoriesNameById(row.supplier_id, data.supplierNames) || 'Unknown';
        const stockNameDefault = row.stock_name || 'Unknown';
        
        cells.push(
        (rowIndex + 1).toString(),
        supplierNameDefault.substring(0, 20),
        stockNameDefault.substring(0, 30),
        formatNumberWithComma(row.quantity || 0),
        formatCurrency(row.cost || 0),
        formatCurrency(row.selling || 0),
        formatCurrency(calculateTotalValue(row))
        );
    }
    
    // Draw cells
    cells.forEach((cell, cellIndex) => {
    xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
    doc.text(cell.toString(), xPos + 2, currentY + 7, {
        maxWidth: colWidths[cellIndex] - 4
    });
    });
    
    currentY += actualRowHeight;
});

return currentY;
};

// Excel export for Inventory Report
export const exportToExcel = (reportData, filters, referenceData) => {
try {
    const { summary, details, topSuppliers, topStockItems, lowQuantityItems, highValueItems } = reportData;
    const { supplierNames = [], stockNames = [] } = referenceData;

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // Summary Sheet
    const summaryData = [
    ['RESTAURANT INVENTORY REPORT SUMMARY'],
    ['Generated:', new Date().toLocaleString()],
    [],
    ['OVERALL SUMMARY'],
    ['Total Items:', summary.total_items || 0],
    ['Total Quantity:', summary.total_quantity || 0],
    ['Total Cost Value:', formatCurrency(summary.total_cost_value || 0)],
    ['Total Selling Value:', formatCurrency(summary.total_selling_value || 0)],
    ['Total Profit:', formatCurrency(summary.total_profit || 0)],
    ['Average Cost per Item:', formatCurrency(summary.average_cost_per_item || 0)],
    ['Average Selling per Item:', formatCurrency(summary.average_selling_per_item || 0)],
    ['Suppliers Count:', summary.suppliers_count || 0],
    ['Stock Items Count:', summary.stock_items_count || 0],
    [],
    ['PROFITABILITY ANALYSIS'],
    ['Profit Margin:', summary.total_selling_value > 0 ? `${((summary.total_profit / summary.total_selling_value) * 100).toFixed(2)}%` : '0%'],
    ['Cost Percentage:', summary.total_selling_value > 0 ? `${((summary.total_cost_value / summary.total_selling_value) * 100).toFixed(2)}%` : '0%'],
    [],
    ['APPLIED FILTERS'],
    filters.dateFrom && filters.dateTo ? [`Date Range: ${filters.dateFrom} to ${filters.dateTo}`] : [],
    filters.supplier_id ? [`Supplier: ${getCategoriesNameById(filters.supplier_id, supplierNames)}`] : [],
    filters.stock_id ? [`Stock Item: ${getCategoriesNameById(filters.stock_id, stockNames)}`] : [],
    filters.min_quantity ? [`Minimum Quantity: ${filters.min_quantity}`] : [],
    filters.max_quantity ? [`Maximum Quantity: ${filters.max_quantity}`] : []
    ].filter(row => row.length > 0);

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Detailed Data Sheet
    if (details && details.length > 0) {
    const detailedHeaders = ['ID', 'Stock Date', 'Supplier', 'Stock Item', 'Quantity', 'Cost', 'Selling', 'Total Cost', 'Total Selling', 'Profit'];
    const detailedData = details.map((item, index) => [
        item.id || index + 1,
        formatDate(item.stock_date),
        getCategoriesNameById(item.supplier_id, supplierNames) || item.supplier_name,
        item.stock_name || 'N/A',
        item.quantity || 0,
        item.cost || 0,
        item.selling || 0,
        (item.quantity || 0) * (item.cost || 0),
        calculateTotalValue(item),
        calculateProfit(item)
    ]);

    const detailedSheet = XLSX.utils.aoa_to_sheet([detailedHeaders, ...detailedData]);
    XLSX.utils.book_append_sheet(workbook, detailedSheet, 'Detailed Data');
    }

    // Top Suppliers Sheet
    if (topSuppliers && topSuppliers.length > 0) {
    const supplierHeaders = ['Supplier', 'Items Count', 'Total Quantity', 'Total Cost', 'Total Value', 'Total Profit'];
    const supplierData = topSuppliers.map(supplier => [
        supplier.supplier_name || 'Unknown',
        supplier.items_count || 0,
        supplier.total_quantity || 0,
        supplier.total_cost || 0,
        supplier.total_value || 0,
        supplier.total_profit || 0
    ]);

    const supplierSheet = XLSX.utils.aoa_to_sheet([supplierHeaders, ...supplierData]);
    XLSX.utils.book_append_sheet(workbook, supplierSheet, 'Top Suppliers');
    }

    // High Value Items Sheet
    if (highValueItems && highValueItems.length > 0) {
    const highValueHeaders = ['Stock Item', 'Supplier', 'Quantity', 'Cost', 'Selling', 'Total Value', 'Profit'];
    const highValueData = highValueItems.map(item => [
        item.stock_name || 'Unknown',
        item.supplier_name || 'Unknown',
        item.quantity || 0,
        item.cost || 0,
        item.selling || 0,
        item.total_value || 0,
        item.profit || 0
    ]);

    const highValueSheet = XLSX.utils.aoa_to_sheet([highValueHeaders, ...highValueData]);
    XLSX.utils.book_append_sheet(workbook, highValueSheet, 'High Value Items');
    }

    // Low Quantity Alerts Sheet
    if (lowQuantityItems && lowQuantityItems.length > 0) {
    const lowQtyHeaders = ['Stock Item', 'Supplier', 'Quantity', 'Cost', 'Selling', 'Status'];
    const lowQtyData = lowQuantityItems.map(item => [
        item.stock_name || 'Unknown',
        item.supplier_name || 'Unknown',
        item.quantity || 0,
        item.cost || 0,
        item.selling || 0,
        item.quantity <= 0 ? 'Out of Stock' : item.quantity <= 10 ? 'Low Stock' : 'Normal'
    ]);

    const lowQtySheet = XLSX.utils.aoa_to_sheet([lowQtyHeaders, ...lowQtyData]);
    XLSX.utils.book_append_sheet(workbook, lowQtySheet, 'Low Quantity Alerts');
    }

    // Generate file name
    const fileName = `restaurant_inventory_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Restaurant Inventory Report Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// PDF export for Inventory Report
// PDF export for Inventory Report (Landscape)
export const exportToPDF = (reportData, filters, referenceData) => {
  try {
    const { summary, details, topSuppliers, highValueItems, lowQuantityItems } = reportData;
    const { supplierNames = [], stockNames = [] } = referenceData;

    // Use landscape orientation (width > height)
    const doc = new jsPDF('l', 'mm', 'a4'); // 'l' for landscape
    
    // Set document properties
    doc.setProperties({
      title: 'Restaurant Inventory Analysis Report',
      subject: 'Restaurant Inventory Detailed Report',
      author: companyInfo.name || 'Restaurant Management System',
      keywords: 'restaurant, inventory, analysis, report',
      creator: companyInfo.name || 'Restaurant Management System'
    });
    
    // Get page dimensions for landscape
    const pageWidth = doc.internal.pageSize.width; // 297mm for A4 landscape
    const pageHeight = doc.internal.pageSize.height; // 210mm for A4 landscape
    const margin = 15;
    const contentWidth = pageWidth - (2 * margin);
    
    // Color definitions (replacing theme.palette)
    const colors = {
      primary: [25, 118, 210], // Blue
      success: [76, 175, 80],  // Green
      warning: [255, 152, 0],  // Orange
      error: [244, 67, 54],    // Red
      grey: [158, 158, 158],   // Grey
      lightGrey: [240, 240, 240],
      white: [255, 255, 255],
      black: [0, 0, 0],
      textSecondary: [80, 80, 80]
    };
    
    // Title section - centered
    doc.setFontSize(22);
    doc.setTextColor(...colors.primary);
    doc.text('RESTAURANT INVENTORY ANALYSIS REPORT', pageWidth / 2, 25, { align: 'center' });
    
    // Generation details - in two columns
    doc.setFontSize(10);
    doc.setTextColor(...colors.textSecondary);
    
    let currentY = 35;
    
    // Left column - Report info
    doc.text(`Report Generated: ${new Date().toLocaleString()}`, margin, currentY);
    currentY += 6;
    doc.text(`Report Type: ${filters.report_type || 'detailed'}`, margin, currentY);
    
    // Right column - Filter info
    let filterY = 35;
    let filterX = pageWidth / 2 + 20;
    
    if (filters.dateFrom && filters.dateTo) {
      doc.text(`Date Range: ${filters.dateFrom} to ${filters.dateTo}`, filterX, filterY);
      filterY += 6;
    }
    
    if (filters.supplier_id) {
      doc.text(`Supplier: ${getCategoriesNameById(filters.supplier_id, supplierNames)}`, filterX, filterY);
      filterY += 6;
    }
    
    if (filters.stock_id) {
      doc.text(`Stock Item: ${getCategoriesNameById(filters.stock_id, stockNames)}`, filterX, filterY);
      filterY += 6;
    }
    
    currentY = Math.max(currentY, filterY) + 15;
    
    // Summary section
    doc.setFontSize(13);
    doc.setTextColor(...colors.primary);
    doc.text('EXECUTIVE SUMMARY', margin, currentY);
    
    // Draw a line under the heading
    doc.setDrawColor(...colors.primary);
    doc.setLineWidth(0.5);
    doc.line(margin, currentY + 2, margin + 60, currentY + 2);
    
    currentY += 15;
    
    // Summary in three columns for better use of landscape space
    doc.setFontSize(10);
    doc.setTextColor(...colors.black);
    
    const col1X = margin;
    const col2X = margin + contentWidth / 3;
    const col3X = margin + (2 * contentWidth / 3);
    
    // Column 1
    doc.text(`Total Items:`, col1X, currentY);
    doc.text(`${formatNumberWithComma(summary.total_items || 0)}`, col1X + 40, currentY, { align: 'right' });
    currentY += 7;
    
    doc.text(`Total Quantity:`, col1X, currentY);
    doc.text(`${formatNumberWithComma(summary.total_quantity || 0)}`, col1X + 40, currentY, { align: 'right' });
    currentY += 7;
    
    doc.text(`Suppliers:`, col1X, currentY);
    doc.text(`${summary.suppliers_count || 0}`, col1X + 40, currentY, { align: 'right' });
    
    // Column 2
    let col2Y = currentY - 14;
    doc.text(`Total Cost Value:`, col2X, col2Y);
    doc.text(`${formatCurrency(summary.total_cost_value || 0)}`, col2X + 50, col2Y, { align: 'right' });
    col2Y += 7;
    
    doc.text(`Total Selling Value:`, col2X, col2Y);
    doc.text(`${formatCurrency(summary.total_selling_value || 0)}`, col2X + 50, col2Y, { align: 'right' });
    col2Y += 7;
    
    doc.text(`Stock Items:`, col2X, col2Y);
    doc.text(`${summary.stock_items_count || 0}`, col2X + 50, col2Y, { align: 'right' });
    
    // Column 3
    let col3Y = currentY - 14;
    doc.text(`Total Profit:`, col3X, col3Y);
    doc.text(`${formatCurrency(summary.total_profit || 0)}`, col3X + 40, col3Y, { align: 'right' });
    col3Y += 7;
    
    const profitMargin = summary.total_selling_value > 0 
      ? ((summary.total_profit / summary.total_selling_value) * 100).toFixed(2)
      : '0.00';
    
    doc.text(`Profit Margin:`, col3X, col3Y);
    doc.text(`${profitMargin}%`, col3X + 40, col3Y, { align: 'right' });
    
    currentY += 20;
    
    // Create two-column layout for insights (top of page)
    const insightsColWidth = contentWidth / 2 - 10;
    const col1Start = margin;
    const col2Start = margin + insightsColWidth + 20;
    
    // Left column - Top Suppliers
    if (topSuppliers && topSuppliers.length > 0) {
      doc.setFontSize(12);
      doc.setTextColor(...colors.primary);
      doc.text('TOP SUPPLIERS BY VALUE', col1Start, currentY);
      
      // Draw a small line under heading
      doc.setDrawColor(...colors.primary);
      doc.setLineWidth(0.3);
      doc.line(col1Start, currentY + 2, col1Start + 80, currentY + 2);
      
      let supplierY = currentY + 10;
      doc.setFontSize(9);
      doc.setTextColor(...colors.black);
      
      topSuppliers.slice(0, 5).forEach((supplier, index) => {
        const supplierName = supplier.supplier_name || 'Unknown';
        const truncatedName = supplierName.length > 25 ? supplierName.substring(0, 22) + '...' : supplierName;
        
        // Row number and name
        doc.text(`${index + 1}. ${truncatedName}`, col1Start, supplierY);
        
        // Value at right
        doc.text(formatCurrency(supplier.total_value || 0), col1Start + insightsColWidth - 5, supplierY, { align: 'right' });
        
        // Progress bar background
        const maxValue = topSuppliers[0]?.total_value || 1;
        const barWidth = (supplier.total_value / maxValue) * (insightsColWidth - 60);
        
        doc.setFillColor(...colors.lightGrey);
        doc.rect(col1Start + 25, supplierY + 3, insightsColWidth - 30, 2, 'F');
        
        doc.setFillColor(...colors.primary);
        doc.rect(col1Start + 25, supplierY + 3, barWidth, 2, 'F');
        
        supplierY += 10;
      });
      
      currentY = supplierY + 5;
    }
    
    // Right column - High Value Items
    if (highValueItems && highValueItems.length > 0) {
      doc.setFontSize(12);
      doc.setTextColor(...colors.primary);
      doc.text('HIGH VALUE ITEMS', col2Start, currentY - (topSuppliers ? 70 : 0));
      
      // Draw a small line under heading
      doc.setDrawColor(...colors.primary);
      doc.setLineWidth(0.3);
      doc.line(col2Start, currentY - (topSuppliers ? 68 : 0), col2Start + 65, currentY - (topSuppliers ? 68 : 0));
      
      let itemY = currentY - (topSuppliers ? 60 : 0);
      doc.setFontSize(9);
      doc.setTextColor(...colors.black);
      
      highValueItems.slice(0, 5).forEach((item, index) => {
        const itemName = item.stock_name || 'Unknown';
        const truncatedName = itemName.length > 25 ? itemName.substring(0, 22) + '...' : itemName;
        
        // Row number and name
        doc.text(`${index + 1}. ${truncatedName}`, col2Start, itemY);
        
        // Value at right
        doc.text(formatCurrency(item.total_value || 0), col2Start + insightsColWidth - 5, itemY, { align: 'right' });
        
        // Profit indicator
        const profit = item.profit || 0;
        doc.setFontSize(8);
        
        // Determine profit color
        let profitColor;
        if (profit > 0) {
          profitColor = colors.success;
        } else if (profit < 0) {
          profitColor = colors.error;
        } else {
          profitColor = colors.grey;
        }
        
        doc.setTextColor(...profitColor);
        doc.text(`Profit: ${formatCurrency(profit)}`, col2Start + 30, itemY + 4);
        doc.setFontSize(9);
        doc.setTextColor(...colors.black);
        
        itemY += 10;
      });
    }
    
    // Low Quantity Alerts - below the two columns
    if (lowQuantityItems && lowQuantityItems.length > 0) {
      currentY = Math.max(
        currentY,
        (topSuppliers ? currentY + 30 : currentY)
      ) + 10;
      
      doc.setFontSize(12);
      doc.setTextColor(...colors.primary);
      doc.text('LOW QUANTITY ALERTS', margin, currentY);
      
      // Draw a small line under heading
      doc.setDrawColor(...colors.primary);
      doc.setLineWidth(0.3);
      doc.line(margin, currentY + 2, margin + 85, currentY + 2);
      
      currentY += 10;
      
      // Display in 3 columns for landscape
      doc.setFontSize(9);
      doc.setTextColor(...colors.black);
      
      const lowQtyColWidth = contentWidth / 3;
      const lowQtyCol1 = margin;
      const lowQtyCol2 = margin + lowQtyColWidth + 10;
      const lowQtyCol3 = margin + (2 * lowQtyColWidth) + 20;
      
      lowQuantityItems.slice(0, 6).forEach((item, index) => {
        const col = index % 3;
        const row = Math.floor(index / 3);
        
        let colX;
        switch(col) {
          case 0: colX = lowQtyCol1; break;
          case 1: colX = lowQtyCol2; break;
          case 2: colX = lowQtyCol3; break;
          default: colX = lowQtyCol1;
        }
        
        const yPos = currentY + (row * 25);
        const itemName = item.stock_name || 'Unknown';
        const truncatedName = itemName.length > 20 ? itemName.substring(0, 18) + '...' : itemName;
        
        // Item name
        doc.text(truncatedName, colX, yPos);
        
        // Quantity with color indicator
        const quantity = item.quantity || 0;
        let statusColor;
        let statusText;
        
        if (quantity <= 0) {
          statusColor = colors.error;
          statusText = 'Out of Stock';
        } else if (quantity <= 10) {
          statusColor = colors.warning;
          statusText = 'Low Stock';
        } else {
          statusColor = colors.success;
          statusText = 'Normal';
        }
        
        doc.setTextColor(...statusColor);
        doc.text(`Qty: ${formatNumberWithComma(quantity)} (${statusText})`, colX, yPos + 6);
        doc.setTextColor(...colors.black);
        
        // Supplier
        const supplierName = item.supplier_name || 'Unknown';
        const truncatedSupplier = supplierName.length > 15 ? supplierName.substring(0, 13) + '...' : supplierName;
        doc.setFontSize(8);
        doc.setTextColor(...colors.grey);
        doc.text(`Supplier: ${truncatedSupplier}`, colX, yPos + 12);
        doc.setFontSize(9);
        doc.setTextColor(...colors.black);
      });
      
      currentY += Math.ceil(lowQuantityItems.slice(0, 6).length / 3) * 25 + 15;
    } else {
      currentY += 20;
    }
    
    // Check if we need a new page for the detailed table
    if (currentY > pageHeight - 80) {
      doc.addPage();
      currentY = margin;
    }
    
    // Detailed Data Table
    if (details && details.length > 0) {
      // Ensure all items have required properties
      const tableData = details.map(item => ({
        ...item,
        supplier_name: item.supplier_name || getCategoriesNameById(item.supplier_id, supplierNames) || 'Unknown',
        stock_name: item.stock_name || 'Unknown',
        quantity: item.quantity || 0,
        cost: item.cost || 0,
        selling: item.selling || 0
      }));
      
      doc.setFontSize(13);
      doc.setTextColor(...colors.primary);
      doc.text('DETAILED INVENTORY DATA', margin, currentY);
      
      // Draw a line under the heading
      doc.setDrawColor(...colors.primary);
      doc.setLineWidth(0.5);
      doc.line(margin, currentY + 2, margin + 95, currentY + 2);
      
      currentY += 10;
      
      // Use wider columns for landscape mode
      const landscapeColWidths = [10, 25, 35, 50, 20, 25, 25, 30, 30, 25];
      const landscapeHeaders = ['#', 'Date', 'Supplier', 'Stock Item', 'Qty', 'Cost', 'Selling', 'Total Cost', 'Total Value', 'Profit'];
      
      // Draw table header
      doc.setFillColor(...colors.primary);
      doc.setTextColor(...colors.white);
      doc.setFont(undefined, 'bold');
      
      let xPos = margin;
      
      // Draw header background
      doc.rect(margin, currentY, contentWidth, 10, 'F');
      
      // Draw header text
      landscapeHeaders.forEach((header, index) => {
        const cellX = xPos + 2;
        doc.text(header, cellX, currentY + 7);
        xPos += landscapeColWidths[index];
      });
      
      currentY += 10;
      
      // Draw table rows
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...colors.black);
      
      let pageNumber = 1;
      details.forEach((row, rowIndex) => {
        // Check for page break
        if (currentY > pageHeight - 30) {
          doc.addPage();
          currentY = margin;
          pageNumber++;
          
          // Redraw header on new page
          doc.setFillColor(...colors.primary);
          doc.setTextColor(...colors.white);
          doc.setFont(undefined, 'bold');
          
          doc.rect(margin, currentY, contentWidth, 10, 'F');
          
          xPos = margin;
          landscapeHeaders.forEach((header, index) => {
            doc.text(header, xPos + 2, currentY + 7);
            xPos += landscapeColWidths[index];
          });
          
          currentY += 10;
          doc.setFont(undefined, 'normal');
          doc.setTextColor(...colors.black);
        }
        
        // Alternate row colors
        if (rowIndex % 2 === 0) {
          doc.setFillColor(...colors.lightGrey);
        } else {
          doc.setFillColor(...colors.white);
        }
        
        // Draw row background
        doc.rect(margin, currentY, contentWidth, 10, 'F');
        
        // Prepare row data
        const supplierName = row.supplier_name || getCategoriesNameById(row.supplier_id, supplierNames) || 'Unknown';
        const stockName = row.stock_name || 'Unknown';
        const truncatedSupplier = supplierName.length > 20 ? supplierName.substring(0, 18) + '...' : supplierName;
        const truncatedStock = stockName.length > 35 ? stockName.substring(0, 33) + '...' : stockName;
        
        const cells = [
          (rowIndex + 1).toString(),
          formatDate(row.stock_date),
          truncatedSupplier,
          truncatedStock,
          formatNumberWithComma(row.quantity || 0),
          formatCurrency(row.cost || 0),
          formatCurrency(row.selling || 0),
          formatCurrency((row.quantity || 0) * (row.cost || 0)),
          formatCurrency(calculateTotalValue(row)),
          formatCurrency(calculateProfit(row))
        ];
        
        // Draw cells
        cells.forEach((cell, cellIndex) => {
          xPos = margin + landscapeColWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
          doc.text(cell.toString(), xPos + 2, currentY + 7, {
            maxWidth: landscapeColWidths[cellIndex] - 4
          });
        });
        
        currentY += 10;
      });
      
      currentY += 10;
    }
    
    // Recommendations on last page
    if (currentY > pageHeight - 50) {
      doc.addPage();
      currentY = margin;
    }
    
    doc.setFontSize(12);
    doc.setTextColor(...colors.primary);
    doc.text('KEY RECOMMENDATIONS', margin, currentY);
    
    // Draw a line under the heading
    doc.setDrawColor(...colors.primary);
    doc.setLineWidth(0.5);
    doc.line(margin, currentY + 2, margin + 90, currentY + 2);
    
    currentY += 10;
    
    doc.setFontSize(10);
    doc.setTextColor(...colors.black);
    
    const recommendations = [];
    
    if (summary.total_selling_value > 0 && (summary.total_profit / summary.total_selling_value) < 0.2) {
      recommendations.push('• Consider increasing selling prices for better profit margins');
    }
    
    if (summary.total_items > 0 && (summary.total_quantity / summary.total_items) < 10) {
      recommendations.push('• Increase stock quantities for better inventory management');
    }
    
    if (highValueItems && highValueItems.length > 0) {
      recommendations.push('• Review high-value items for restocking opportunities');
    }
    
    if (lowQuantityItems && lowQuantityItems.length > 0) {
      recommendations.push('• Monitor low-quantity items for timely reordering');
    }
    
    recommendations.push('• Regularly review supplier performance and stock turnover');
    recommendations.push('• Consider bulk purchasing for frequently used items');
    recommendations.push('• Implement inventory tracking system for real-time monitoring');
    
    // Display recommendations in two columns for landscape
    const recColWidth = contentWidth / 2 - 10;
    const recCol1 = margin;
    const recCol2 = margin + recColWidth + 20;
    let recY = currentY;
    
    recommendations.forEach((rec, index) => {
      const col = index < Math.ceil(recommendations.length / 2) ? recCol1 : recCol2;
      const row = index < Math.ceil(recommendations.length / 2) 
        ? index 
        : index - Math.ceil(recommendations.length / 2);
      
      doc.text(rec, col, recY + (row * 7));
    });
    
    // Footer
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(...colors.grey);
      doc.text(`Page ${i} of ${totalPages} | Generated by ${companyInfo.name || 'Restaurant Management System'}`, 
               pageWidth / 2, pageHeight - 10, { align: 'center' });
      doc.text(new Date().toLocaleDateString(), pageWidth - margin, pageHeight - 10, { align: 'right' });
    }
    
    const fileName = `restaurant_inventory_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Restaurant Inventory Report PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
  }
};
// CSV export for Inventory Report
export const exportToCSV = (reportData, filters, referenceData) => {
try {
    const { summary, details } = reportData;
    const { supplierNames = [], stockNames = [] } = referenceData;

    // Create summary CSV
    const summaryCsv = [
    ['RESTAURANT INVENTORY REPORT SUMMARY'],
    ['Generated:', new Date().toLocaleString()],
    [],
    ['Metric', 'Value'],
    ['Total Items', summary.total_items || 0],
    ['Total Quantity', summary.total_quantity || 0],
    ['Total Cost Value', formatCurrency(summary.total_cost_value || 0)],
    ['Total Selling Value', formatCurrency(summary.total_selling_value || 0)],
    ['Total Profit', formatCurrency(summary.total_profit || 0)],
    ['Average Cost per Item', formatCurrency(summary.average_cost_per_item || 0)],
    ['Average Selling per Item', formatCurrency(summary.average_selling_per_item || 0)],
    ['Suppliers Count', summary.suppliers_count || 0],
    ['Stock Items Count', summary.stock_items_count || 0],
    ['Profit Margin', summary.total_selling_value > 0 ? `${((summary.total_profit / summary.total_selling_value) * 100).toFixed(2)}%` : '0%'],
    [],
    ['FILTERS APPLIED'],
    filters.dateFrom && filters.dateTo ? ['Date Range', `${filters.dateFrom} to ${filters.dateTo}`] : [],
    filters.supplier_id ? ['Supplier', getCategoriesNameById(filters.supplier_id, supplierNames)] : [],
    filters.stock_id ? ['Stock Item', getCategoriesNameById(filters.stock_id, stockNames)] : []
    ].filter(row => row.length > 0);

    // Create detailed CSV
    let detailedCsv = [];
    if (details && details.length > 0) {
    const detailedHeaders = ['ID', 'Stock Date', 'Supplier', 'Stock Item', 'Quantity', 'Cost', 'Selling', 'Total Cost', 'Total Selling', 'Profit'];
    detailedCsv = [
        ['DETAILED INVENTORY DATA'],
        [],
        detailedHeaders,
        ...details.map(item => [
        item.id || '',
        formatDate(item.stock_date),
        getCategoriesNameById(item.supplier_id, supplierNames) || item.supplier_name,
        item.stock_name || 'N/A',
        item.quantity || 0,
        item.cost || 0,
        item.selling || 0,
        (item.quantity || 0) * (item.cost || 0),
        calculateTotalValue(item),
        calculateProfit(item)
        ])
    ];
    }

    // Combine all CSV data
    const allCsvData = [...summaryCsv, [], ...detailedCsv];
    
    const csvContent = allCsvData
    .map(row => row.map(cell => {
        if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
        return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
    }).join(','))
    .join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `restaurant_inventory_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('Restaurant Inventory Report CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};

// Export multiple report types
export const exportInventoryReport = {
toExcel: exportToExcel,
toPDF: exportToPDF,
toCSV: exportToCSV
};

export default exportInventoryReport;
